import { useCallback } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { type PartialProduct } from '../../graphql';
import type { AppStackParamList } from '../../navigation/AppNavigation.props';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ────────────────────────────────────────────────────────────────────────────────

export const useNavigateToCustomization = (props: Params) => {
  const { restaurantSlug, deliveryAddressId } = props;

  const { track } = useTelemetry();
  const { navigate } = useNavigation<NavigationProp<AppStackParamList>>();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── HELPERS ─────────────────────────────────────────────────────

  const sendTelemetryEvent = useCallback(
    (product: PartialProduct) => {
      track('pdp.view', {
        entryPoint: 'menu',
        selectedProduct: telemetryProductFromInput({
          ...product,
          location: restaurantSlug,
        }),
      });
    },
    [restaurantSlug, track],
  );

  const navigateToCustomization = useCallback(
    (productSlug: string) => {
      if (!restaurantSlug) return;

      navigate('Modal', {
        screen: 'Customization',
        params: { productSlug, restaurantSlug },
      });
    },
    [navigate, restaurantSlug],
  );

  const navigateToCustomizationDelivery = useCallback(
    (productSlug: string) => {
      if (!deliveryAddressId) return;

      // Block delivery pdp navigation when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      navigate('Modal', {
        screen: 'CustomizationDelivery',
        params: {
          productSlug,
          addressId: deliveryAddressId,
        },
      });
    },
    [
      deliveryAddressId,
      inFlightDeliveryOrderId,
      navigate,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );

  // ─────────────────────────────────────────────────────────────────

  return useCallback(
    (product: PartialProduct) => {
      const { slug: productSlug = '' } = product;

      sendTelemetryEvent(product);

      if (deliveryAddressId) {
        navigateToCustomizationDelivery(productSlug);
        return;
      }

      navigateToCustomization(productSlug);
    },
    [
      sendTelemetryEvent,
      deliveryAddressId,
      navigateToCustomizationDelivery,
      navigateToCustomization,
    ],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type Params = {
  restaurantSlug?: string;
  deliveryAddressId?: string;
};
